import * as XLSX from 'xlsx';
import type { ControlBulkDraftRow } from './controlDraftValidation.ts';
import { CONTROL_TEXT_MIN_LENGTH, type ControlFormValues } from './schema.ts';

function asText(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function asBool(value: unknown): boolean {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  return false;
}

function asNullableBool(value: unknown): boolean | null {
  if (value === null || value === undefined || value === '') return null;
  return asBool(value);
}

function normalizeAssertions(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const legacyMap: Record<string, string> = {
    valuation: 'accuracy & valuation',
    'accuracy-valuation': 'accuracy & valuation',
    occurrence: 'occurence',
    obligation: 'rights & obligation',
    'rights-obligation': 'rights & obligation',
    'presentation-disclosure': 'presentation & disclosure',
  };

  return value
    .map((a) => String(a).trim().toLowerCase())
    .map((a) => legacyMap[a] ?? a)
    .filter(Boolean);
}

function normalizeFrequency(value: unknown): string {
  const parsed = asText(value).toLowerCase().replace(/\s+/g, ' ');
  if (!parsed) return '';

  const map: Record<string, string> = {
    annual: 'annual',
    'half-yearly': 'half-yearly',
    quarterly: 'quarterly',
    monthly: 'monthly',
    'semi-monthly': 'semi-monthly',
    weekly: 'weekly',
    'bi-weekly': 'bi-weekly',
    daily: 'daily',
    'event-driven': 'event-based',
    'event based': 'event-based',
    'event-based': 'event-based',
  };

  return map[parsed] ?? parsed.replace(/ /g, '-');
}

function normalizeEnumWithMap(value: unknown, map: Record<string, string>): string {
  const parsed = asText(value);
  const normalized = parsed.toLowerCase();
  if (!normalized) return '';
  return map[normalized] ?? parsed;
}

export function controlRecordToFormValues(record: Record<string, unknown>): ControlFormValues {
  const itgcDomainObj =
    (record.itgc_domain as { id?: string } | undefined) ??
    (record.itgcDomain as { id?: string } | undefined);
  const rawItgcSystems = Array.isArray(record.itgc_systems)
    ? (record.itgc_systems as Array<{ id?: string }>)
    : Array.isArray(record.itgcSystems)
      ? (record.itgcSystems as Array<{ id?: string }>)
      : [];
  const mappedItgcSystemIds = rawItgcSystems
    .map((row) => asText(row?.id))
    .filter(Boolean);
  return {
    controlId: asText(record.client_control_id ?? record.control_id ?? record.controlId),
    title: asText(record.title),
    objective: asText(record.objective),
    controlWordingText: asText(record.control_wording_text ?? record.control_wording),
    controlType: normalizeEnumWithMap(record.control_type ?? record.controlType, {
      manual: 'manual',
      automated: 'automated',
      'it-dependent-manual': 'it-dependent-manual',
    }) || '',
    frequency: normalizeFrequency(record.frequency) || '',
    significance: normalizeEnumWithMap(record.significance, {
      key: 'key',
      'non-key': 'non-key',
    }) || '',
    classification: normalizeEnumWithMap(record.classification, {
      business: 'business',
      itgc: 'itgc',
    }) || '',
    controlNature: normalizeEnumWithMap(record.control_nature ?? record.controlNature, {
      preventive: 'preventive',
      detective: 'detective',
    }) || '',
    mrcControl: asBool(record.mrc_control ?? record.mrcControl),
    assertions: normalizeAssertions(record.assertions),
    department: asText(record.department),
    controlOwner: asText(record.control_owner ?? record.controlOwner),
    keyReportIds: (() => {
      const raw = record.key_report_ids ?? record.keyReportIds;
      if (Array.isArray(raw) && raw.length > 0) {
        return raw.map((id) => asText(id)).filter(Boolean);
      }
      const reports = record.key_reports ?? record.keyReports;
      if (!Array.isArray(reports)) return [];
      return reports
        .map((item) => {
          if (item && typeof item === 'object' && 'id' in item) {
            return asText((item as { id?: unknown }).id);
          }
          return asText(item);
        })
        .filter(Boolean);
    })(),
    externalAuditorReliance: asNullableBool(
      record.external_auditor_reliance ?? record.externalAuditorReliance
    ),
    itgcDomainId: asText(record.itgc_domain_id ?? record.itgcDomainId ?? itgcDomainObj?.id),
    itgcSystemIds: mappedItgcSystemIds,
  } as ControlFormValues;
}

/** Normalized test steps array from a control API record. */
export function getControlTestSteps(
  record: Record<string, unknown> | null | undefined
): unknown[] {
  if (!record) return [];
  const steps = record.test_steps ?? record.testSteps;
  return Array.isArray(steps) ? steps : [];
}

/** Number of configured test steps on a control record from the API. */
export function getControlTestStepCount(
  record: Record<string, unknown> | null | undefined
): number {
  return getControlTestSteps(record).length;
}

/**
 * Display lifecycle for control detail and list status badges.
 * Active when API status is active or at least one test step exists (draft with steps still runnable).
 */
export function getControlEffectiveStatus(
  record: Record<string, unknown> | null | undefined
): 'draft' | 'active' {
  if (!record) return 'draft';
  const apiStatus = String(record.status ?? 'draft').toLowerCase().trim();
  if (apiStatus === 'active') return 'active';
  if (getControlTestStepCount(record) > 0) return 'active';
  return 'draft';
}

/** Draft control with no test steps — restrict details page to Overview tab only. */
export function isDraftControlAwaitingTestConfig(
  record: Record<string, unknown> | null | undefined
): boolean {
  if (!record) return false;
  const status = String(record.status ?? 'draft').toLowerCase();
  return status === 'draft' && getControlTestStepCount(record) === 0;
}

/** Control has no test steps yet (draft or active) — show Configure Test. */
export function controlNeedsTestConfiguration(
  record: Record<string, unknown> | null | undefined
): boolean {
  return getControlTestStepCount(record) === 0;
}

export const CONFIGURE_TEST_FIELD_LABELS = {
  title: 'Title',
  controlWording: 'Control Wording',
  objective: 'Objective',
} as const;

export type ConfigureTestRequiredField = keyof typeof CONFIGURE_TEST_FIELD_LABELS;

/** Title, wording, and objective must meet {@link CONTROL_TEXT_MIN_LENGTH} for configure-test (except title: non-empty only). */
function meetsConfigureTestMinText(value: unknown): boolean {
  return asText(value).length >= CONTROL_TEXT_MIN_LENGTH;
}

function missingConfigureTestFieldsFromParts(parts: {
  title?: unknown;
  controlWording?: unknown;
  objective?: unknown;
}): ConfigureTestRequiredField[] {
  const missing: ConfigureTestRequiredField[] = [];
  if (!asText(parts.title)) missing.push('title');
  if (!meetsConfigureTestMinText(parts.controlWording)) missing.push('controlWording');
  if (!meetsConfigureTestMinText(parts.objective)) missing.push('objective');
  return missing;
}

/** Fields that must be present on the control before Configure Test can run. */
export function getMissingConfigureTestFields(
  record: Record<string, unknown> | null | undefined
): ConfigureTestRequiredField[] {
  if (!record) return ['title', 'controlWording', 'objective'];
  return missingConfigureTestFieldsFromParts({
    title: record.title ?? record.control_name,
    controlWording: record.control_wording_text ?? record.control_wording,
    objective: record.objective,
  });
}

export function getMissingConfigureTestFieldsFromValues(
  values: Partial<ControlFormValues> | null | undefined
): ConfigureTestRequiredField[] {
  if (!values) return ['title', 'controlWording', 'objective'];
  return missingConfigureTestFieldsFromParts({
    title: values.title,
    controlWording: values.controlWordingText,
    objective: values.objective,
  });
}

export function canOpenConfigureTest(
  record: Record<string, unknown> | null | undefined
): boolean {
  return getMissingConfigureTestFields(record).length === 0;
}

/** Tooltip when Configure Test is disabled due to missing overview fields. */
export function formatConfigureTestBlockedMessage(
  missing: ConfigureTestRequiredField[]
): string {
  const labels = missing.map((key) => {
    const base = CONFIGURE_TEST_FIELD_LABELS[key];
    if (key === 'title') return base;
    return `${base} (at least ${CONTROL_TEXT_MIN_LENGTH} characters)`;
  });
  if (labels.length === 0) return '';
  if (labels.length === 1) return `Add ${labels[0]} to configure test.`;
  const last = labels[labels.length - 1];
  const rest = labels.slice(0, -1).join(', ');
  return `Add ${rest} and ${last} to configure test.`;
}

/** Control is ready to run tests (effective active + at least one test step). */
export function canRunControlTest(
  record: Record<string, unknown> | null | undefined
): boolean {
  if (!record) return false;
  return getControlEffectiveStatus(record) === 'active' && getControlTestStepCount(record) > 0;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when value looks like a SOX control UUID (not a human control_id such as CTL_000023). */
export function isControlUuid(value: string | null | undefined): boolean {
  return UUID_RE.test(String(value ?? '').trim());
}

/** Canonical SOX control UUID for API routes and update bodies. */
export function resolveControlUuid(
  record: Record<string, unknown> | null | undefined,
  routeId?: string
): string {
  const candidates = [
    record?.uuid,
    record?.control_uuid,
    record?.controlUuid,
    record?.id,
    record?.sox_control_id,
    routeId,
  ]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean);

  const uuid = candidates.find(isControlUuid);
  if (uuid) return uuid;

  return candidates[0] ?? '';
}

/** Cleared optional strings/enums are sent as `null` so the API can unset them on edit. */
function optionalStringToApi(value: unknown): string | null {
  const text = asText(value);
  return text === '' ? null : text;
}

export function formValuesToApiPayload(
  values: ControlFormValues,
  options?: {
    controlUuid?: string;
    riskMappings?: { riskId: string; processIds: string[] }[];
    itgcRiskId?: string | null;
    itgcRiskIds?: string[];
  }
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    title: values.title.trim(),
    objective: optionalStringToApi(values.objective),
    control_wording_text: values.controlWordingText.trim(),
    mrc_control: !!values.mrcControl,
    assertions: values.assertions ?? [],
    department: optionalStringToApi(values.department),
    control_owner: optionalStringToApi(values.controlOwner),
    key_report_ids: Array.isArray(values.keyReportIds)
      ? values.keyReportIds.map((id) => String(id).trim()).filter(Boolean)
      : [],
    source: 'manual',
    client_control_id: asText(values.controlId).trim(),
    control_type: optionalStringToApi(values.controlType),
    frequency: optionalStringToApi(values.frequency),
    significance: optionalStringToApi(values.significance),
    classification: optionalStringToApi(values.classification),
    control_nature: optionalStringToApi(values.controlNature),
    external_auditor_reliance: values.externalAuditorReliance ?? null,
  };

  const controlUuid = options?.controlUuid?.trim();
  if (controlUuid) {
    payload.uuid = controlUuid;
  }
  const normalizedClassification = String(values.classification ?? '').trim().toLowerCase();
  if (normalizedClassification === 'itgc') {
    payload.itgc_domain_id = optionalStringToApi(values.itgcDomainId);
    payload.itgc_system_ids = Array.isArray(values.itgcSystemIds)
      ? values.itgcSystemIds.map((id) => String(id).trim()).filter(Boolean)
      : [];
    const riskIds = Array.isArray(options?.itgcRiskIds)
      ? options.itgcRiskIds.map((id) => String(id).trim()).filter(Boolean)
      : options?.itgcRiskId
        ? [String(options.itgcRiskId).trim()].filter(Boolean)
        : [];
    payload.itgc_risk_ids = riskIds;
    payload.itgc_risk_id = riskIds[0] ?? null;
  } else {
    payload.risk_mappings = (options?.riskMappings ?? []).map((item) => ({
      risk_id: String(item.riskId ?? '').trim(),
      process_ids: Array.isArray(item.processIds)
        ? item.processIds.map((id) => String(id).trim()).filter(Boolean)
        : [],
    }));
  }

  return payload;
}

export function formatControlSourceLabel(source: string): string {
  const s = source.trim().toLowerCase();
  if (!s) return '';
  if (s === 'manual') return 'Manual';
  if (s === 'import' || s === 'bulk_import') return 'Import';
  if (s === 'ai' || s === 'extraction') return 'AI Extraction';
  return source.trim();
}

export function formatControlStatusLabel(status: string): string {
  const s = status.trim().toLowerCase();
  if (!s) return 'Draft';
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
}

export function formatFrequencyLabel(value: string): string {
  const key = asText(value).toLowerCase().replace(/\s+/g, '-');
  const labels: Record<string, string> = {
    annual: 'Annual',
    'half-yearly': 'Half Yearly',
    quarterly: 'Quarterly',
    monthly: 'Monthly',
    'semi-monthly': 'Semi Monthly',
    weekly: 'Weekly',
    'bi-weekly': 'Bi Weekly',
    daily: 'Daily',
    'event-based': 'Event Based',
    'event-driven': 'Event Based',
  };
  return labels[key] ?? value;
}

const normalizeHeaderKey = (key: string): string =>
  String(key || '')
    .toLowerCase()
    .replace(/[\s_/]+/g, '');

function getSpreadsheetCell(row: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) {
      return String(row[key]).trim();
    }
  }
  const entries = Object.entries(row);
  for (const key of keys) {
    const want = normalizeHeaderKey(key);
    const hit = entries.find(([k]) => normalizeHeaderKey(k) === want);
    if (hit && hit[1] !== undefined && hit[1] !== null) {
      return String(hit[1]).trim();
    }
  }
  return '';
}

export function spreadsheetHasRequiredHeaders(rows: Record<string, unknown>[]): boolean {
  if (rows.length === 0) return false;
  const keys = Object.keys(rows[0] ?? {}).map(normalizeHeaderKey);
  const hasTitle = keys.some((k) => k === 'title' || k.includes('controltitle'));
  const hasDescription = keys.some(
    (k) => k === 'description' || k.includes('wording') || (k.includes('description') && !k.includes('objective'))
  );
  return hasTitle && hasDescription;
}

export async function parseControlSpreadsheet(file: File): Promise<Record<string, unknown>[]> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
}

export function spreadsheetToBulkDraftRows(fileData: Record<string, unknown>[]): ControlBulkDraftRow[] {
  const stamp = Date.now();
  return fileData.map((row, index) => ({
    id: `control-bulk-${stamp}-${index}`,
    title: getSpreadsheetCell(row, ['Title', 'title', 'control_title', 'Control Title']),
    controlWordingText: getSpreadsheetCell(row, [
      'Description / Wordings',
      'Description',
      'control_wording_text',
      'Control Wording',
      'Wordings',
      'control_wording',
    ]),
  }));
}
